import React, { useState } from 'react';
import { CImg, CFormGroup } from '@coreui/react';
import { useField } from 'formik';
import { useDropzone } from 'react-dropzone';
import emptyAvatar from '../../../../assets/images/empty-avatar.png';


import './ImageInput.scss';

interface ImageInputProps {
  name: string;
  disabled?: boolean;
  maxImageSize?: number;
}

const FILE_TO_LARGE_CODE = 'file-too-large';
const FILE_TO_LARGE_ERROR_MESSAGE = 'File is too big. Max image size: 2MB';

const ImageInput = (props: ImageInputProps) => {
  const { name, maxImageSize } = props;

  const [field, meta, helpers] = useField(name);
  const [sizeError, setSizeError] = useState(false);

  const handleImageChange = files => {
    setSizeError(false);
    const objectUrl = URL.createObjectURL(files[0]);
    const file = files[0];
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);

    reader.onloadend = () => {
      helpers.setValue({
        url: objectUrl,
        file: reader.result,
        type: file.type
      });
    }
  };

  const isInvalid = () => meta.touched && meta.error || sizeError;

  const getErrorMessage = () => {
    if (meta.touched && meta.error) {
      return meta.error;
    }
    if (sizeError) {
      return FILE_TO_LARGE_ERROR_MESSAGE
    }
    return '';
  }

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/jpeg, image/png',
    maxFiles: 1,
    maxSize: maxImageSize,
    onDropAccepted: handleImageChange,
    onDropRejected: (data) => {
      const isSizeLimitExceeded = data?.[0]?.errors.filter(i => i.code === FILE_TO_LARGE_CODE).length > 0;
      if (isSizeLimitExceeded) {
        setSizeError(isSizeLimitExceeded);
      }
    }
  });

  return (
    <CFormGroup className="d-flex flex-column mb-2">
      <div className='glympser-imageWrapper'>
        <div className='glympser-imageContainer'>
          <CImg className="image-element mb-2" src={field.value?.url || emptyAvatar} />
        </div>
      </div>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <div { ...getRootProps({ className: 'dropzone' }) }>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <input {...(getInputProps() as any)} />
        <p className='mb-0'>Drag and drop a file here, or click to select a file</p>
        <em>(Only *.jpeg and *.png images will be accepted)</em>
      </div>
      { isInvalid() && <div className="invalid-feedback d-block">{ getErrorMessage() }</div> }
    </CFormGroup>
  );
};

ImageInput.defaultProps = {
  disabled: false,
  maxImageSize: 2000000
};

export default ImageInput;
